import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Form, 
    Input, 
    Button, 
    Upload, 
    Avatar, 
    Typography, 
    Select,
    Row,
    Col,
    message,
    Spin,
    Modal,
    Divider,
    Space
} from 'antd';
import { 
    UserOutlined, 
    UploadOutlined, 
    LockOutlined,
    SaveOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    DeleteOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBaseURL } from '../config.js';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const UserSettings = () => {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profileForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [preferencesForm] = Form.useForm();
    const baseURL = getBaseURL();

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('http://localhost:8080/api/user/profile', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                const userData = response.data.data;
                setUserInfo(userData);
                
                // 设置表单初始值
                profileForm.setFieldsValue({
                    username: userData.username,
                    email: userData.email
                });
                
                preferencesForm.setFieldsValue({
                    language: userData.preferences?.language || 'English',
                    depth: userData.preferences?.depth || 'Medium'
                });
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            if (error.response?.status === 401) {
                message.error('Please login again');
                navigate('/login');
            } else {
                message.error('Failed to load user profile');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleProfileUpdate = async (values) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put('http://localhost:8080/api/user/profile', {
                username: values.username,
                email: values.email,
                avatar: userInfo?.avatar || null
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('Profile updated successfully');
                fetchUserProfile(); // 重新获取用户信息
            } else {
                message.error(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error.response?.data?.error) {
                const errorCode = error.response.data.error;
                const errorMessage = error.response.data.message;
                
                switch (errorCode) {
                    case 'USERNAME_EXISTS':
                        message.error('Username already exists');
                        break;
                    case 'EMAIL_EXISTS':
                        message.error('Email already exists');
                        break;
                    case 'VALIDATION_ERROR':
                        message.error('Please check your input data');
                        break;
                    default:
                        message.error(errorMessage || 'Failed to update profile');
                }
            } else {
                message.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (values) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put('http://localhost:8080/api/user/password', {
                current_password: values.currentPassword,
                new_password: values.newPassword
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('Password updated successfully');
                passwordForm.resetFields();
            } else {
                message.error(response.data.message || 'Failed to update password');
            }
        } catch (error) {
            console.error('Error updating password:', error);
            if (error.response?.data?.error) {
                const errorCode = error.response.data.error;
                const errorMessage = error.response.data.message;
                
                switch (errorCode) {
                    case 'CURRENT_PASSWORD_WRONG':
                        message.error('Current password is incorrect');
                        break;
                    case 'VALIDATION_ERROR':
                        message.error('Please check your input data');
                        break;
                    default:
                        message.error(errorMessage || 'Failed to update password');
                }
            } else {
                message.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handlePreferencesUpdate = async (values) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put('http://localhost:8080/api/user/preferences', {
                language: values.language,
                depth: values.depth
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('Preferences updated successfully');
                fetchUserProfile(); // 重新获取用户信息
            } else {
                message.error(response.data.message || 'Failed to update preferences');
            }
        } catch (error) {
            console.error('Error updating preferences:', error);
            if (error.response?.data?.error) {
                message.error(error.response.data.message || 'Failed to update preferences');
            } else {
                message.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAvatarUpload = async (file) => {
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('avatar', file);

            await axios.post(`https://${baseURL}/user/avatar`, formData, {
                headers: {
                    'Authorization': token,
                    'Content-Type': 'multipart/form-data'
                }
            });

            message.success('Avatar updated successfully');
            fetchUserProfile(); // 重新获取用户信息
        } catch (error) {
            console.error('Error uploading avatar:', error);
            message.error('Failed to upload avatar');
        }
        
        return false; // 阻止默认上传行为
    };

    const handleDeleteAccount = () => {
        confirm({
            title: 'Are you sure you want to delete your account?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone. All your data will be permanently deleted.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                // 显示密码确认对话框
                Modal.confirm({
                    title: 'Enter your password to confirm',
                    content: (
                        <Input.Password 
                            placeholder="Enter your password"
                            id="delete-password-input"
                        />
                    ),
                    onOk: async () => {
                        const password = document.getElementById('delete-password-input').value;
                        if (!password) {
                            message.error('Please enter your password');
                            return;
                        }
                        
                        try {
                            const token = localStorage.getItem('token');
                            const response = await axios.delete('http://localhost:8080/api/user/account', {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                },
                                data: {
                                    password: password
                                }
                            });

                            if (response.data.success) {
                                message.success('Account deleted successfully');
                                localStorage.removeItem('token');
                                navigate('/login');
                            } else {
                                message.error(response.data.message || 'Failed to delete account');
                            }
                        } catch (error) {
                            console.error('Error deleting account:', error);
                            if (error.response?.data?.error) {
                                const errorCode = error.response.data.error;
                                const errorMessage = error.response.data.message;
                                
                                switch (errorCode) {
                                    case 'CURRENT_PASSWORD_WRONG':
                                        message.error('Password is incorrect');
                                        break;
                                    default:
                                        message.error(errorMessage || 'Failed to delete account');
                                }
                            } else {
                                message.error('Network error. Please try again.');
                            }
                        }
                    }
                });
            }
        });
    };

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '60vh' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!userInfo) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <Text>Failed to load user information</Text>
            </div>
        );
    }

    return (
        <div style={{ 
            padding: '24px',
            background: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)'
        }}>
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
                <Title level={2} style={{ marginBottom: 24 }}>Account Settings</Title>

                {/* 个人信息设置 */}
                <Card title="Profile Information" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={24}>
                        <Col span={6}>
                            <div style={{ textAlign: 'center' }}>
                                <Avatar 
                                    size={100} 
                                    src={userInfo.avatar}
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: '#1890ff', marginBottom: 16 }}
                                />
                                <Upload
                                    beforeUpload={handleAvatarUpload}
                                    showUploadList={false}
                                    accept="image/*"
                                >
                                    <Button icon={<UploadOutlined />} size="small">
                                        Change Avatar
                                    </Button>
                                </Upload>
                            </div>
                        </Col>
                        <Col span={18}>
                            <Form
                                form={profileForm}
                                layout="vertical"
                                onFinish={handleProfileUpdate}
                            >
                                <Form.Item
                                    name="username"
                                    label="Username"
                                    rules={[
                                        { required: true, message: 'Please enter your username' },
                                        { min: 3, message: 'Username must be at least 3 characters' }
                                    ]}
                                >
                                    <Input placeholder="Enter username" />
                                </Form.Item>

                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[
                                        { required: true, message: 'Please enter your email' },
                                        { type: 'email', message: 'Please enter a valid email' }
                                    ]}
                                >
                                    <Input placeholder="Enter email" />
                                </Form.Item>

                                <Form.Item>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        loading={saving}
                                        icon={<SaveOutlined />}
                                    >
                                        Update Profile
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Col>
                    </Row>
                </Card>

                {/* 密码修改 */}
                <Card title="Change Password" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Form
                        form={passwordForm}
                        layout="vertical"
                        onFinish={handlePasswordChange}
                    >
                        <Form.Item
                            name="currentPassword"
                            label="Current Password"
                            rules={[{ required: true, message: 'Please enter your current password' }]}
                        >
                            <Input.Password 
                                placeholder="Enter current password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <Form.Item
                            name="newPassword"
                            label="New Password"
                            rules={[
                                { required: true, message: 'Please enter your new password' },
                                { min: 6, message: 'Password must be at least 6 characters' }
                            ]}
                        >
                            <Input.Password 
                                placeholder="Enter new password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirmPassword"
                            label="Confirm New Password"
                            dependencies={['newPassword']}
                            rules={[
                                { required: true, message: 'Please confirm your new password' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password 
                                placeholder="Confirm new password"
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={saving}
                                icon={<LockOutlined />}
                            >
                                Change Password
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

                {/* 学习偏好设置 */}
                <Card title="Learning Preferences" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Form
                        form={preferencesForm}
                        layout="vertical"
                        onFinish={handlePreferencesUpdate}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="language"
                                    label="Preferred Language"
                                    rules={[{ required: true, message: 'Please select a language' }]}
                                >
                                    <Select placeholder="Select language">
                                        <Option value="English">English</Option>
                                        <Option value="Chinese">中文</Option>
                                        <Option value="Spanish">Español</Option>
                                        <Option value="French">Français</Option>
                                        <Option value="German">Deutsch</Option>
                                        <Option value="Japanese">日本語</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="depth"
                                    label="Learning Depth"
                                    rules={[{ required: true, message: 'Please select learning depth' }]}
                                >
                                    <Select placeholder="Select depth">
                                        <Option value="Beginner">Beginner</Option>
                                        <Option value="Intermediate">Intermediate</Option>
                                        <Option value="Advanced">Advanced</Option>
                                        <Option value="Expert">Expert</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={saving}
                                icon={<SaveOutlined />}
                            >
                                Update Preferences
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

                {/* 危险操作 */}
                <Card title="Danger Zone" style={{ borderRadius: 12 }}>
                    <div style={{ padding: '16px 0' }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                            Once you delete your account, there is no going back. Please be certain.
                        </Text>
                        <Button 
                            danger 
                            onClick={handleDeleteAccount}
                        >
                            Delete Account
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default UserSettings; 